<script setup lang="ts">
import Chip from 'primevue/chip';

import { type File } from '../../../../api/resource/types/file';
import { ButtonSeverity } from '../../../../presets/button/types/buttonServerity';

defineProps<{
  files: File[];
  chipColor: string;
  chipLabel: string;
  deleteDisabled: boolean;
}>();

const emits = defineEmits<{
  removeFile: [File];
}>();

const handleImageError = (event: Event): void => {
  const target = event.target as HTMLImageElement;
  if (target) {
    target.style.display = 'none';
  }
};
</script>

<template>
  <div class="files-list">
    <div
      v-for="file in files"
      :key="file.name"
      class="files-list__item"
    >
      <div class="files-list__content">
        <div class="files-list__thumbnail">
          <img
            v-if="file.type.includes('image')"
            :src="(file as unknown as File).objectURL"
            class="files-list__image"
            @error="handleImageError"
          />
        </div>
        <div class="files-list__info">
          <span class="files-list__name">{{ file.name }}</span>
          <div class="files-list__status">
            <Chip :class="`files-list__chip ${chipColor}`"> {{ chipLabel }} </Chip>
          </div>
        </div>
      </div>
      <Button
        :icon="'pi pi-times'"
        class="files-list__delete-button"
        :disabled="deleteDisabled"
        :severity="ButtonSeverity.danger"
        @click="() => emits('removeFile', file)"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.files-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  max-width: 100%;

  &__item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
  }

  &__content {
    min-height: 7rem;
    overflow: hidden;
    display: flex;
    flex: 1;
    justify-content: flex-start;
    align-items: center;
    width: 50%;

    @media (min-width: $media-min-tablet) {
      width: 100%;
    }
  }

  &__thumbnail {
    position: relative;
    width: 7rem;
    height: 7rem;
    flex-shrink: 0;
  }

  &__image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 0.5rem;
  }

  &__info {
    padding-left: 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  &__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
  }

  &__status {
    display: flex;
  }

  &__chip {
    padding: 0.5rem 1rem;
    color: $white;
  }

  &__delete-button {
    margin-right: 1rem;
  }
}
</style>
